/**
 * Submission domain models mirroring the backend API.
 *
 * Defines the SubmissionBase/SubmissionCreate/SubmissionDisplay/
 * SubmissionWithBounty hierarchy, SubmissionStatus union, title/description
 * length limits, payment processing response shapes, and AI verification
 * result/extracted-data types. Consumed by the submission API modules
 * (userSubmission, bountySubmission, submissionVerification, admin),
 * submission pages, and shared item components.
 */
import { Bounty } from "@/types/bounty";
import { PaymentStatus, StripePaymentDetails } from "@/types/payment";
import { CreatedUpdatedAt } from "@/types/createdUpdatedAt";

export const SUBMISSION_TITLE_MAX_LENGTH = 80;
export const SUBMISSION_DESCRIPTION_MAX_LENGTH = 1000;

export interface SubmissionBase {
    id?: number;
    status?: SubmissionStatus;
    title: string;
    bountyId: number;
    price: number;
    description: string;
    productUrl?: string;
    porsuiUserId: number;
}

export interface SubmissionCreate extends SubmissionBase {
    imageUrls: string[];
}

export interface SubmissionDisplay extends SubmissionBase, CreatedUpdatedAt {
    s3ImageUrls: string[];
}

export interface SubmissionWithBounty extends SubmissionDisplay {
    bounty: Bounty;
}

export type SubmissionType = "submission";

export type SubmissionStatus =
    | "pending"
    | "validated"
    | "accepted"
    | "rejected"
    | "ai_rejected"
    | "ai_processing";

export const SubmissionStatuses: readonly SubmissionStatus[] = [
    "pending",
    "validated",
    "accepted",
    "rejected",
    "ai_rejected",
    "ai_processing",
];

export interface ProcessAcceptSubmissionPaymentResponse {
    status: SubmissionStatus;
    paymentStatus: PaymentStatus;
    submissionId: number;
    bountyId: number;
    paymentDetails: StripePaymentDetails;
}

export interface PollSubmissionPaymentStatusResponse {
    paymentStatus: PaymentStatus;
    paymentErrorMessage: string | null;
}

export interface SubmissionVerificationResult {
    passed: boolean;
    failureReasons: string[];
}

export interface SubmissionVerificationExtractedData {
    title: string;
    description: string;
    price: string;
    available: boolean;
    imageUrls: string[];
    isProductPage: boolean;
    productUrl: string;
}

export interface NestedSubmissionVerificationExtractedDataResponse {
    extractedData: SubmissionVerificationExtractedData;
}
